import type { Action } from '@reduxjs/toolkit';

import type { SetCredentialsPayload } from '../store/authentication.slice';

/**
 * The Walkie-Talkie system for cross-tab communication.
 * We use the browser's BroadcastChannel API to let tabs talk to each other.
 *
 * Types of messages:
 * - `LOGIN`: Someone logged in on another tab. (Contains the user details).
 * - `TOKEN_REFRESHED`: Another tab silently got a new token. (Contains the fresh token).
 * - `LOGOUT`: Another tab logged out. (Tells this tab to clean up and go to the login screen).
 *
 * Security Note: Sending the access token over this radio is safe. If a hacker (XSS) is
 * already inside the tab, they can read the memory anyway. The real security win is that
 * the token is just a transient message flying through the air, and NEVER saved to the
 * hard drive (`localStorage`).
 */

export type AuthBroadcastMessage =
  | { type: 'LOGIN'; payload: SetCredentialsPayload }
  | { type: 'TOKEN_REFRESHED'; token: string }
  | { type: 'LOGOUT' };

const CHANNEL_NAME = 'oir-auth';

// Fallback for older browsers or testing environments (like Vitest) that don't support
// BroadcastChannel. If it's missing, the app just safely behaves like a normal single-tab app.
const isBroadcastChannelSupported = (): boolean => typeof BroadcastChannel !== 'undefined';

let channel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (!isBroadcastChannelSupported()) {
    return null;
  }

  // Some browsers expose the class but throw a SecurityError when you actually construct it,
  // e.g. inside a sandboxed iframe or under strict privacy/storage-partitioning settings. We
  // catch that here so it degrades to single-tab behaviour instead of crashing the whole app
  // (same defensive approach we use for localStorage in the inactivity clock).
  try {
    channel ??= new BroadcastChannel(CHANNEL_NAME);
    return channel;
  } catch (error) {
    console.warn('BroadcastChannel is blocked by browser security settings:', error);
    return null;
  }
};

export const postAuthMessage = (message: AuthBroadcastMessage): void => {
  getChannel()?.postMessage(message);
};

/**
 * Turns on the Walkie-Talkie receiver.
 * It listens for messages from OTHER tabs (a tab never hears its own messages).
 * Returns a cleanup function to turn off the radio (perfect for React's `useEffect`).
 */
export const subscribeToAuthMessages = (handler: (message: AuthBroadcastMessage) => void): (() => void) => {
  const activeChannel = getChannel();

  if (!activeChannel) {
    return () => undefined;
  }

  const handleIncomingMessage = (event: MessageEvent<AuthBroadcastMessage>): void => handler(event.data);
  activeChannel.addEventListener('message', handleIncomingMessage);

  return () => activeChannel.removeEventListener('message', handleIncomingMessage);
};

type SkipBroadcastMeta = { meta: { skipBroadcast: true } };

/**
 * A "Do Not Broadcast" sticker for Redux actions.
 * If we apply an action that we just heard on the radio, we stick this on it
 * so our Outbound middleware doesn't echo it back out (preventing an infinite ping-pong loop).
 * We also use this during app startup (F5) so we don't spam other tabs.
 */

type ActionWithMeta = { meta?: { skipBroadcast?: boolean } };

export const skipBroadcast = <A extends Action>(action: A): A & SkipBroadcastMeta => {
  const existingMeta = (action as unknown as ActionWithMeta).meta;

  const safeMeta = typeof existingMeta === 'object' && existingMeta !== null ? existingMeta : {};

  return {
    ...action,
    meta: {
      ...safeMeta,
      skipBroadcast: true,
    },
  };
};

export const isBroadcastSuppressed = (action: unknown): boolean =>
  (action as ActionWithMeta)?.meta?.skipBroadcast === true;
